<script setup lang="ts">
import { FolderList, MovePassage } from '~/apis/passageStorage'
import type { folderListData } from '~/apis/passageStorage/type'
import {useCache} from "~/common/useCache";

// GA Analytics 추가
const { trackEvent } = useAnalytics()

const app = useAppStore()
const passageStorage = usePassageStorage()

const folderList = ref<folderListData[]>([])
const selectedFolder = ref('0')

// 선택한 폴더로 지문 이동
const moveToSelectedFolder = async () => {
  const moveStartTime = Date.now()
  const targetFolder = folderList.value.find(f => String(f.folder_idx) === selectedFolder.value)
  
  try {
    const res = await MovePassage({
      UM_ID: app.user.UM_ID,
      EDP_IDX: passageStorage.state.value.moveToFolder.move_edp_idx,
      TARGET_FOLDER_IDX: selectedFolder.value,
    })
    if (res.data) {
      // GA 이벤트: 지문 이동 완료
      try {
        trackEvent('passagestorage_move_completed', {
          event_category: 'item_interaction',
          passage_id: passageStorage.state.value.moveToFolder.move_edp_idx,
          target_folder_id: selectedFolder.value,
          target_folder_name: targetFolder?.folder_name || '지문 보관함',
          move_time_ms: Date.now() - moveStartTime,
          available_folders_count: folderList.value.length,
          um_idx: app.user.UM_IDX ?? '',
          timestamp: new Date().toISOString()
        })
      } catch (error) {
        console.warn('GA tracking failed:', error)
      }
      
      passageStorage.closeMoveToFolder()
      passageStorage.reload()
    } else {
      console.log('폴더 이동 실패')
    }
  } catch (err) {
    console.log('[ERROR] 폴더 이동 오류 - ', err)
  } finally {
    //
  }
}

// 폴더 선택 변경 핸들러
const handleFolderSelection = (folderId: string) => {
  const previousSelection = selectedFolder.value
  selectedFolder.value = folderId
  const selectedFolderInfo = folderList.value.find(f => String(f.folder_idx) === folderId)
  
  // GA 이벤트: 폴더 선택
  try {
    trackEvent('passagestorage_move_folder_selected', {
      event_category: 'ui_interaction',
      previous_folder_id: previousSelection,
      selected_folder_id: folderId,
      selected_folder_name: selectedFolderInfo?.folder_name || '지문 보관함',
      passage_count_in_target: selectedFolderInfo?.passage_cnt || 0,
      available_folders_count: folderList.value.length,
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString()
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }
}

// 이동 취소 핸들러  
const handleCancelMove = () => {
  // GA 이벤트: 이동 취소
  try {
    trackEvent('passagestorage_move_cancelled', {
      event_category: 'ui_interaction',
      passage_id: passageStorage.state.value.moveToFolder.move_edp_idx,
      selected_folder_id: selectedFolder.value,
      had_selection: selectedFolder.value !== '0',
      available_folders_count: folderList.value.length,
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString()
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }
  
  passageStorage.closeMoveToFolder()
}
// 폴더 목록 조회
const getFolderList = async (current?: string) => {
  try {
    const res = await FolderList({
      UM_ID: app.user.UM_ID,
    })
    if (res.data) {
      folderList.value = current
        ? res.data.data.filter(
            (folder) => String(folder.folder_idx) !== String(current),
          )
        : res.data.data.filter((folder) => String(folder.folder_idx) !== '')
    }
  } catch (err) {
    console.log('[ERROR] 폴더 목록 조회 오류 - ', err)
  }
}

onMounted(async () => {
  // GA 이벤트: 이동 팝업 열림
  try {
    trackEvent('passagestorage_move_dialog_opened', {
      event_category: 'ui_interaction',
      passage_id: passageStorage.state.value.moveToFolder.move_edp_idx,
      action_source: 'move_menu_click',
      um_idx: app.user.UM_IDX ?? '',
      timestamp: new Date().toISOString()
    })
  } catch (error) {
    console.warn('GA tracking failed:', error)
  }
  
  const route = useRoute()
  const folderIdx = route.params.idx
  if (folderIdx) {
    await getFolderList(folderIdx)
  } else {
    await getFolderList()
  }
})
</script>

<template>
  <!-- 폴더 이동하기 -->
  <div class="layer moveToFolder active">
    <div class="mask"></div>
    <div class="inner">
      <section class="sample fullHeight">
        <header class="">
          <h2>폴더 이동하기</h2>
          <a class="layerClose" @click="handleCancelMove"
            >닫기</a
          >
        </header>
        <main>
          <div class="popDataNone" v-if="folderList.length === 0">
            <p>폴더가 없습니다</p>
          </div>
          <div v-else class="passageLoadList">
            <ul>
              <li v-for="item in folderList" :key="item.folder_idx">
                <label class="label curp">
                  <span class="img">
                    <img v-if="item.folder_idx" src="/assets/images/board/img_folder.svg" alt="폴더" />
                    <img v-else src="/assets/images/board/img_text_bank.svg" alt="폴더" />
                  </span>
                  <span class="wrap">
                    <span class="tit">{{
                      item.folder_idx ? item.folder_name : '지문 보관함'
                    }}</span>
                    <span class="ul">
                      <span v-if="item.folder_idx" class="li">{{
                        item.reg_date
                      }}</span>
                      <span class="li">지문 {{ item.passage_cnt }}개</span>
                    </span>
                  </span>
                  <input
                    v-model="selectedFolder"
                    type="radio"
                    name="radio"
                    :value="item.folder_idx"
                    @change="handleFolderSelection(item.folder_idx)"
                  />
                </label>
              </li>
            </ul>
          </div>
        </main>
        <footer>
          <div class="btnWrap">
            <a
              :class="`btnStyle wide ${selectedFolder === '0' ? 'disable' : ''}`"
              @click="moveToSelectedFolder"
            >
              선택한 폴더로 이동
            </a>
          </div>
        </footer>
      </section>
    </div>
  </div>
  <!-- /폴더 이동하기 -->
</template>
